import { Suspense, lazy } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import Layout from "./Layout";
import HomePage from "./components/HomePage/HomePage";
import RequireAuth from "./components/RequireAuth";
import { ThemeProvider } from "./context/ThemeProvider";
import { SupportChatProvider } from "./context/SupportChatContext";
import { CartProvider } from "./context/CartContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import { DownloadQueueProvider } from "./context/DownloadQueueContext";
import SEO from "./components/SEO";

const AdminLogin = lazy(() => import("./AdminPages/AdminLogin"));
const CreatorPortalPage = lazy(
  () => import("./components/Creator/CreatorPortalPage"),
);
const CreatorJoinPage = lazy(
  () => import("./components/CreatorJoin/CreatorJoinPage"),
);
const CreatorDashboard = lazy(
  () => import("./components/Creator/CreatorDashboard"),
);
const CategoryPage = lazy(() => import("./components/CategoryPage/CategoryPage"));
const Dashboard = lazy(() => import("./AdminPages/Dashboard"));
const Pricing = lazy(() => import("./components/Pricing/Pricing"));
const Privacy = lazy(() => import("./components/PolicyPages/Privacy"));
const Terms = lazy(() => import("./components/PolicyPages/Terms"));
const Refund = lazy(() => import("./components/PolicyPages/Refund"));
const AboutPage = lazy(() => import("./components/About/AboutPage"));
const PaymentPage = lazy(() => import("./components/Pricing/PaymentPage"));
const SubscriptionUpgradePage = lazy(
  () => import("./components/Pricing/SubscriptionUpgradePage"),
);
const Faqs = lazy(() => import("./components/About/Faqs"));
const SearchPage = lazy(() => import("./components/SearchPage/SearchPage"));
const Subscribe = lazy(() => import("./components/Subscribe"));
const ShipmentDelivery = lazy(() => import("./components/PolicyPages/ShipmentDelivery"));
const ContactUs = lazy(() => import("./components/ContactUs"));
const OneTimePaymentPage = lazy(() => import("./components/Pricing/OneTimePaymentPage"));
const Chatbot = lazy(() => import("./components/Chatbot"));
const AdminCreate = lazy(() => import("./AdminCreate"));
const CollectionsPage = lazy(() => import("./components/CollectionsPage"));
const VideoPageRoute = lazy(() => import("./components/VideoPage/VideoPageRoute"));
const CollectionViewPage = lazy(() => import("./components/CollectionViewPage/CollectionViewPage"));
const CartPage = lazy(() => import("./components/Cart/CartPage"));
const CartCheckoutPage = lazy(() => import("./components/Cart/CartCheckoutPage"));
const DedicatedSubmissionReview = lazy(
  () => import("./AdminPages/AdminComponents/DedicatedSubmissionReview"),
);

const AppRoutes = () => {
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation;
  const noIndexRoute =
    /^\/(admin|creator(?!\/join\/?$)|dashboard|admincreate|cart|chatbot|subscribe|subscriptionpayment|subscriptionupgrade|onetimepurchase)(\/|$)/.test(
      location.pathname,
    );

  return (
    <>
      {noIndexRoute && (
        <SEO
          title="Shotkut"
          description="Private or transactional Shotkut page."
          noIndex={true}
        />
      )}
      <Suspense fallback={null}>
        <Routes location={backgroundLocation || location}>
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />

            <Route path="admin" element={<AdminLogin />} />
            <Route path="creator" element={<CreatorPortalPage />} />
            <Route path="creator/join" element={<CreatorJoinPage />} />

            <Route element={<RequireAuth />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="creator/dashboard" element={<CreatorDashboard />} />
              <Route
                path="onetimepurchase/:id"
                element={<OneTimePaymentPage />}
              />
              <Route path="subscriptionpayment" element={<PaymentPage />} />
              <Route
                path="subscriptionupgrade"
                element={<SubscriptionUpgradePage />}
              />
              <Route path="cart/checkout" element={<CartCheckoutPage />} />
              <Route
                path="admin/creator-submissions/:submissionId"
                element={<DedicatedSubmissionReview />}
              />
            </Route>

            <Route path="cart" element={<CartPage />} />

            <Route path="search" element={<SearchPage />} />
            <Route path="video/:slug" element={<VideoPageRoute />} />

            <Route path="category/:id" element={<CategoryPage />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="collections" element={<CollectionsPage />} />
            <Route path="collection" element={<CollectionViewPage />} />
            <Route path="collection/:slug" element={<CollectionViewPage />} />

            <Route path="subscribe" element={<Subscribe />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="faqs" element={<Faqs />} />
            <Route path="contactus" element={<ContactUs />} />

            <Route path="privacypolicy" element={<Privacy />} />
            <Route path="termsandconditions" element={<Terms />} />
            <Route path="refundpolicy" element={<Refund />} />
            <Route path="shipmentanddelivery" element={<ShipmentDelivery />} />
            <Route path="admincreate" element={<AdminCreate />} />
            <Route path="chatbot" element={<Chatbot />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>

      {backgroundLocation && (
        <Suspense fallback={null}>
          <Routes>
            <Route path="/video/:slug" element={<VideoPageRoute />} />
          </Routes>
        </Suspense>
      )}
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <CartProvider>
        <FavoritesProvider>
          <DownloadQueueProvider>
            <SupportChatProvider>
              <ToastContainer />
              <AppRoutes />
            </SupportChatProvider>
          </DownloadQueueProvider>
        </FavoritesProvider>
      </CartProvider>
    </ThemeProvider>
  );
}

export default App;
